<template>
    <div>
        <div class="text-sm text-gray-500 mb-4">
            <router-link to="/content/learn/courses">Courses</router-link>
            > Course Name > Module Name > Edit Lesson: {{ lesson.title }}
        </div>
    </div>
    <form
        @submit="submitForm"
        id="lessonForm"
        class="shadow rounded-x"
        v-if="lesson"
    >
        <div
            class="flex flex-row flex-wrap gap-2 items-center px-6 py-2 bg-gray-200 rounded-t-md"
        >
            <div class="text-xs flex flex-row items-center">
                <span class="font-semibold"
                    >ID<span class="hidden md:inline-flex">:&nbsp;</span></span
                ><span class="hidden md:inline-flex">{{ lesson.id }}</span>
                <CopyAction :value="lesson.id" />
            </div>
            <div class="text-xs flex flex-row items-center">
                <span class="font-semibold"
                    >UUID<span class="hidden md:inline-flex"
                        >:&nbsp;</span
                    ></span
                ><span class="hidden md:inline-flex">{{ lesson.uuid }}</span>
                <CopyAction :value="lesson.uuid" />
                <span class="font-semibold"
                    >Status<span class="hidden md:inline-flex"
                        >:&nbsp;</span
                    ></span
                >
                <span
                    v-if="isPublished"
                    class="hidden font-bold md:inline-flex text-green-500"
                    >{{ lesson.status.toUpperCase() }}</span
                >
                <span
                    v-if="!isPublished"
                    class="hidden font-bold md:inline-flex text-red-500"
                    >{{ lesson.status.toUpperCase() }}</span
                >
                <span class="hidden md:inline-flex">
                    <ToggleSwitch
                        v-model="isPublished"
                        size="sm"
                        @click="handleToggleClick"
                        class="mx-2"
                    />
                </span>
            </div>
        </div>
        <div class="bg-white space-y-6 py-9 px-6">
            <div class="grid md:grid-cols-2 gap-6">
                <div>
                    <label
                        for="title"
                        class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                    >
                        Title
                    </label>
                    <input
                        type="text"
                        name="title"
                        id="title"
                        v-model="lesson.title"
                        class="h-12"
                        required
                    />
                </div>
                <div>
                    <label
                        for="slug"
                        class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                    >
                        Slug
                    </label>
                    <input
                        type="text"
                        name="slug"
                        id="slug"
                        v-model="lesson.slug"
                        class="h-12"
                        required
                    />
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div>
                    <label
                        for="description"
                        class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                    >
                        Description
                    </label>
                    <textarea
                        name="description"
                        id="description"
                        rows="8"
                        class="bg-white rounded-lg w-full"
                        v-model="lesson.description"
                    />
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div>
                    <label
                        for="video_id"
                        class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                    >
                        Video
                    </label>
                    <WistiaPreviewer
                        id="video_id"
                        name="video_id"
                        :videoPath="lesson.video_path"
                        v-on:path-changed="updateVideo"
                    />
                </div>
                <div>
                    <label
                        for="video_id"
                        class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                    >
                        Video Length (in seconds)
                    </label>
                    <input
                        type="text"
                        name="video_length"
                        id="video_length"
                        v-model="lesson.video_length"
                        class="h-12"
                    />
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div>
                    <label
                        for="quiz"
                        class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                    >
                        <div>Quiz</div>
                        <div
                            class="text-xs text-red-600 font-normal"
                            v-if="error_msg"
                        >
                            {{ error_msg }}
                        </div>
                    </label>
                    <SelectDropdown
                        v-model="lesson.quiz_id"
                        name="quiz_id"
                        theme="flat"
                        :required="false"
                    >
                        <option value="">Select an option</option>
                        <option v-for="e in exam_options" :value="e.id">
                            {{ e.name }}
                        </option>
                    </SelectDropdown>
                </div>
            </div>
            <div>
                <label
                    for="content"
                    class="flex items-center text-sm leading-7 font-medium ml-2 space-x-4"
                >
                    <div>Content</div>
                </label>
                <Editor name="content" ref="editor" :value="initial" />
            </div>

            <div class="pt-6">
                <div class="flex flex-wrap items-center justify-between mb-4">
                    <h3
                        class="flex items-center text-md leading-7 font-bold ml-2 space-x-4"
                    >
                        Additional Resources
                    </h3>

                    <Menu as="div" class="relative inline-block text-left">
                        <MenuButton
                            class="inline-flex items-center gap-3 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                        >
                            <PlusIcon class="w-6 h-6" />
                            <span>Add Resource</span>
                            <ChevronDownIcon
                                class="h-5 w-5"
                                aria-hidden="true"
                            />
                        </MenuButton>

                        <transition
                            enter-active-class="transition duration-100 ease-out"
                            enter-from-class="transform scale-95 opacity-0"
                            enter-to-class="transform scale-100 opacity-100"
                            leave-active-class="transition duration-75 ease-in"
                            leave-from-class="transform scale-100 opacity-100"
                            leave-to-class="transform scale-95 opacity-0"
                        >
                            <MenuItems
                                class="absolute right-0 z-20 mt-3 w-[300px] max-w-[90vw] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl focus:outline-none"
                            >
                                <MenuItem
                                    v-for="(
                                        resourceOption, index
                                    ) in resourceOptions"
                                    :key="resourceOption.key"
                                    v-slot="{ active }"
                                >
                                    <button
                                        type="button"
                                        class="flex w-full items-center gap-4 px-4 py-4 text-left transition"
                                        :class="[
                                            active ? 'bg-gray-50' : '',
                                            selectedResourceKey ===
                                            resourceOption.key
                                                ? 'bg-blue-50'
                                                : '',
                                            index < resourceOptions.length - 1
                                                ? 'border-b border-gray-300'
                                                : '',
                                        ]"
                                        @click="
                                            selectResourceOption(
                                                resourceOption.key
                                            )
                                        "
                                    >
                                        <div class="flex items-start gap-3">
                                            <svg-icon
                                                :icon="resourceOption.icon"
                                                size="xl"
                                                :class="
                                                    resourceOption.bgColor +
                                                    ' p-2 rounded-lg'
                                                "
                                                aria-hidden="true"
                                            />
                                            <div>
                                                <p
                                                    class="text-md font-semibold text-gray-900"
                                                >
                                                    {{ resourceOption.title }}
                                                </p>
                                                <p
                                                    class="text-sm text-gray-600"
                                                >
                                                    {{
                                                        resourceOption.subtitle
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                </MenuItem>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>

                <div>
                    <div v-if="selectedResourceKey === 'flashcards'">
                        <Flashcards
                            :lessonId="lesson.id"
                            @cancel="clearSelectedResource"
                            @attach="attachResource"
                        />
                    </div>
                    <div v-else-if="selectedResourceKey === 'slides'">
                        <Slides
                            :lessonId="lesson.id"
                            @cancel="clearSelectedResource"
                            @attach="attachResource"
                        />
                    </div>
                    <div
                        v-else-if="selectedResourceKey === 'interactive_image'"
                    >
                        <InteractiveImage
                            :lessonId="lesson.id"
                            @cancel="clearSelectedResource"
                            @create="handleResourceCreated"
                            @attach="attachResource"
                        />
                    </div>
                    <div v-else-if="resources.length === 0">
                        <Default />
                    </div>
                </div>

                <div
                    v-if="resources.length > 0"
                    class="mt-4 overflow-hidden rounded-lg border border-gray-300 bg-white"
                    role="list"
                    aria-label="Attached resources"
                >
                    <div
                        v-for="resource in resources"
                        :key="resource.id"
                        role="listitem"
                        class="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-5 last:border-b-0"
                    >
                        <div class="flex min-w-0 flex-1 items-center gap-4">
                            <div
                                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-md"
                                :class="getResourceIconClass(resource.type)"
                                aria-hidden="true"
                            >
                                <svg-icon
                                    :icon="getResourceIcon(resource.type)"
                                    size="md"
                                />
                            </div>
                            <div class="min-w-0">
                                <p class="font-semibold text-gray-800">
                                    {{ resource.title }}
                                </p>
                                <p class="text-sm text-gray-500">
                                    <span
                                        class="font-semibold normal-case text-gray-600"
                                    >
                                        {{
                                            getResourceOptionLabel(
                                                resource.type
                                            )
                                        }}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            @click="removeResource(resource.id)"
                            class="text-red-600 transition hover:text-red-700"
                            :aria-label="`Remove resource: ${resource.title}`"
                        >
                            <XMarkIcon class="h-7 w-7" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="bg-gray-50 border-t border-gray-200 rounded-b-md p-6 flex justify-end gap-4"
        >
            <button
                v-if="lesson.video_id"
                class="button rounded-md bg-white border text-gray-700 hover:bg-gray-100"
                type="button"
                :disabled="isRegeneratingPreview"
                @click="regeneratePreview"
            >
                {{ isRegeneratingPreview ? "Requesting preview…" : "Regenerate preview" }}
            </button>
            <button class="main button rounded-md" type="submit">Submit</button>
        </div>
    </form>
</template>
<script lang="ts">
import axios from "axios";
import { mapMutations } from "vuex";
import WistiaPreviewer from "../../../_elements/WistiaPreviewer.vue";
import SelectDropdown from "../../../_elements/SelectDropdown.vue";
import Editor from "../../../_elements/Editor.vue";
import ToggleSwitch from "@/components/_elements/ToggleSwitch.vue";
import Default from "./Default.vue";
import Flashcards from "./Flashcards.vue";
import Slides from "./Slides.vue";
import InteractiveImage from "./InteractiveImage.vue";
import { PlusIcon, XMarkIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";

export default {
    components: {
        ToggleSwitch,
        WistiaPreviewer,
        SelectDropdown,
        Editor,
        Default,
        Flashcards,
        Slides,
        InteractiveImage,
        PlusIcon,
        XMarkIcon,
        Menu,
        MenuButton,
        MenuItems,
        MenuItem,
        ChevronDownIcon,
    },
    props: {
        lessonUuid: String,
        moduleUuid: String,
    },
    data(): any {
        return {
            lesson: {
                content: null,
                description: null,
                id: null,
                uuid: "",
                quiz_id: null,
                slug: null,
                title: null,
                video_id: null,
                video_length: "",
                video_path: "",
                status: "",
            },
            exam_options: null,
            error_msg: null,
            initial: null,
            resources: [],
            isAttachingResource: false,
            isRegeneratingPreview: false,
            selectedResourceKey: null,
            resourceOptions: [
                {
                    key: "flashcards",
                    title: "Flashcards",
                    subtitle: "Attach an existing flashcard set",
                    icon: "square-3-stack-3d",
                    bgColor: "bg-yellow-100",
                },
                {
                    key: "slides",
                    title: "Slides",
                    subtitle: "Attach an existing slide deck",
                    icon: "led-tv",
                    bgColor: "bg-purple-100",
                },
                {
                    key: "interactive_image",
                    title: "Interactive Image",
                    subtitle: "Upload a zip from Articulate",
                    icon: "photo",
                    bgColor: "bg-blue-100",
                },
            ],
        };
    },
    computed: {
        lessonContent() {
            return this.$store.state.admin.lessonContent;
        },
        isPublished() {
            return this.lesson.status === "published";
        },
    },
    watch: {
        lessonContent: function (val) {
            this.lesson.content = JSON.stringify(val);
        },
    },
    methods: {
        clearSelectedResource() {
            this.selectedResourceKey = null;
        },
        selectResourceOption(optionKey) {
            this.selectedResourceKey = optionKey;
        },
        updateVideo(path, id) {
            this.lesson.video_path = path;
            this.lesson.video_id = id;
        },
        attachResource(payload) {
            const lessonId = Number(this.lesson.id);
            const resourceId = Number(payload?.resource_id);

            if (!lessonId || !resourceId) {
                this.$events.$emit("toastNotification", {
                    action: "attach",
                    heading: "Unable to Attach Resource",
                    status: "error",
                    message: "Lesson ID or resource selection is missing.",
                });

                return;
            }

            if (this.isAttachingResource) {
                return;
            }

            this.isAttachingResource = true;

            axios
                .post(`/api/admin/content/learn/resources/attach`, {
                    lesson_id: lessonId,
                    resource_id: resourceId,
                })
                .then(() => {
                    this.getLesson();
                    this.$events.$emit("toastNotification", {
                        action: "attach",
                        heading: "Resource Attached",
                        status: "success",
                        message:
                            payload?.resource?.title ||
                            "Resource attached to lesson successfully.",
                    });
                    this.selectedResourceKey = null;
                })
                .catch((error) => {
                    this.$events.$emit("toastNotification", {
                        action: "attach",
                        heading: "Error Attaching Resource",
                        status: "error",
                        message:
                            error?.response?.data?.message ||
                            "Failed to attach resource to lesson.",
                    });
                })
                .finally(() => {
                    this.isAttachingResource = false;
                });
        },
        handleResourceCreated() {
            this.getLesson();
            this.selectedResourceKey = null;
        },
        regeneratePreview() {
            if (this.isRegeneratingPreview) {
                return;
            }

            if (
                !window.confirm(
                    "Make a new 60-second preview for this lesson? The current preview is replaced once the new one is ready.",
                )
            ) {
                return;
            }

            this.isRegeneratingPreview = true;

            axios
                .post(
                    `/api/admin/content/learn/lessons/${this.lessonUuid}/regenerate-preview`,
                )
                .then((response) => {
                    this.$events.$emit("toastNotification", {
                        action: "regenerate",
                        heading: "Preview Requested",
                        status: "success",
                        message: response?.data?.message,
                    });
                })
                .catch((error) => {
                    this.$events.$emit("toastNotification", {
                        action: "regenerate",
                        heading: "Unable to Regenerate Preview",
                        status: "error",
                        message:
                            error?.response?.data?.message ||
                            "Failed to request a new preview.",
                    });
                })
                .finally(() => {
                    this.isRegeneratingPreview = false;
                });
        },
        getLesson() {
            axios
                .get(`/api/admin/content/learn/lessons/${this.lessonUuid}`)
                .then((response) => {
                    this.exam_options = response.data.exam_options;
                    this.lesson = response.data.lesson;
                    this.resources = response.data.resources || [];
                    this.initial = JSON.parse(this.lesson.content);
                });
        },
        getResourceIcon(type) {
            const iconMap = {
                flashcards: "square-3-stack-3d",
                slides: "led-tv",
                slides_type_one: "led-tv",
                slides_type_two: "led-tv",
                interactive_images: "photo",
                pdf: "document",
            };
            return iconMap[type] || "document";
        },
        getResourceIconClass(type) {
            const classMap = {
                flashcards: "bg-yellow-100",
                slides: "bg-purple-100",
                slides_type_one: "bg-purple-100",
                slides_type_two: "bg-purple-100",
                pdf: "bg-green-100",
                interactive_images: "bg-blue-100",
            };
            return classMap[type] || "bg-gray-100";
        },
        normalizeResourceOptionKey(type) {
            if (type === "slides_type_one" || type === "slides_type_two") {
                return "slides";
            }

            if (type === "interactive_images") {
                return "interactive_image";
            }

            return type;
        },
        getResourceOption(type) {
            const optionKey = this.normalizeResourceOptionKey(type);

            return this.resourceOptions.find(
                (option) => option.key === optionKey
            );
        },
        getResourceOptionLabel(type) {
            return (
                this.getResourceOption(type)?.title ||
                (type || "").replace(/_/g, " ")
            );
        },
        removeResource(resourceId) {
            axios
                .post("/api/admin/content/learn/resources/detach", {
                    lesson_id: this.lesson.id,
                    resource_id: resourceId,
                })
                .then(() => {
                    const index = this.resources.findIndex(
                        (r) => r.id === resourceId
                    );
                    if (index !== -1) {
                        this.resources.splice(index, 1);
                    }
                    this.$events.$emit("toastNotification", {
                        action: "removed",
                        heading: "Resource Removed",
                        status: "success",
                    });
                })
                .catch((error) => {
                    this.$events.$emit("toastNotification", {
                        action: "removed",
                        heading: "Error Removing Resource",
                        status: "error",
                        message: error.response?.data?.message,
                    });
                });
        },
        getExamOptions() {
            axios.get(`/api/admin/content/learn/exams`).then((response) => {
                this.exam_options = response.data;
            });
        },
        handleToggleClick(e) {
            if (this.lesson.status === "published") {
                this.lesson.status = "draft";
            } else {
                this.lesson.status = "published";
            }

            this.submitForm(e);
        },
        submitForm(e) {
            e.preventDefault();
            // The preview is managed by CreateWistiaPreviewVideo, which may replace (and delete) it while this
            // page is open, so never send back the preview fields loaded when the page opened
            const {
                preview_video_id,
                preview_video_transcript,
                ...lessonFields
            } = this.lesson;
            axios
                .post(
                    `/api/admin/content/learn/lessons/${this.lessonUuid}`,
                    lessonFields
                )
                .then((response) => {
                    if (response.status < 400) {
                        this.lesson = response.data;
                        this.$events.$emit("toastNotification", {
                            action: "saved",
                            heading: "Lesson Successfully Updated",
                            status: "success",
                        });
                        this.$router.push({
                            name: "module_lessons",
                            params: { uuid: this.moduleUuid },
                        });
                    }
                })
                .catch((e) => {
                    this.$events.$emit("toastNotification", {
                        action: "saved",
                        heading: "Error Updating Lesson",
                        status: "error",
                        message: e.response.data.message,
                    });
                });
        },
        ...mapMutations(["hideBladePage"]),
    },
    mounted() {
        this.hideBladePage();
        this.getLesson();
        this.getExamOptions();
    },
};
</script>
